import { Building, Check, Plus, ChevronDown } from 'lucide-react';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { orgAvatarStyle } from '@/lib/orgColor';

interface WorkspaceSwitcherProps {
  collapsed?: boolean;
}

export function WorkspaceSwitcher({ collapsed = false }: WorkspaceSwitcherProps) {
  const { organizations, currentOrganization, switchOrganization } = useOrganizations();
  const navigate = useNavigate();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            aria-label="Switch organization"
          >
            <Avatar className="h-8 w-8 rounded-md">
              <AvatarFallback
                className="text-xs font-semibold rounded-md"
                style={orgAvatarStyle(currentOrganization?.name)}
              >
                {getInitials(currentOrganization?.name || 'Org')}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Organizations</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {organizations?.map((org) => (
            <DropdownMenuItem
              key={org.id}
              className="cursor-pointer flex items-center justify-between"
              onClick={() => switchOrganization(org.id)}
            >
              <span className="truncate">{org.name}</span>
              {currentOrganization?.id === org.id && (
                <Check className="h-4 w-4 ml-2" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => navigate('/organizations/new')}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>Create Organization</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-2 hover:bg-accent"
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <Avatar className="h-7 w-7 shrink-0 rounded-md">
              <AvatarFallback
                className="text-xs font-semibold rounded-md"
                style={orgAvatarStyle(currentOrganization?.name)}
              >
                {getInitials(currentOrganization?.name || 'Org')}
              </AvatarFallback>
            </Avatar>
            <span className="truncate font-medium text-sm">
              {currentOrganization?.name || 'Select Org'}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations?.map((org) => (
          <DropdownMenuItem
            key={org.id}
            className="cursor-pointer flex items-center justify-between"
            onClick={() => switchOrganization(org.id)}
          >
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{org.name}</span>
            </div>
            {currentOrganization?.id === org.id && (
              <Check className="h-4 w-4 ml-2" />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => navigate('/organizations/new')}
        >
          <Plus className="mr-2 h-4 w-4" />
          <span>Create Organization</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
